/**
 * reCAPTCHA setup and management utilities
 */

import { getAuth, RecaptchaVerifier } from 'firebase/auth';
import { FirebaseApp } from 'firebase/app';

/**
 * Configuration for reCAPTCHA
 */
export interface RecaptchaConfig {
  containerId: string;
  size?: 'invisible' | 'normal';
  callback?: () => void;
  expiredCallback?: () => void;
}

/**
 * Initialize an invisible reCAPTCHA verifier
 * @param app - Firebase app instance
 * @param config - reCAPTCHA configuration
 * @returns RecaptchaVerifier instance or null if initialization fails
 */
export const initializeRecaptcha = (
  app: FirebaseApp,
  config: RecaptchaConfig
): RecaptchaVerifier | null => {
  try {
    // Check if container exists
    const container = document.getElementById(config.containerId);
    if (!container) {
      return null;
    }

    const auth = getAuth(app);
    const recaptchaVerifier = new RecaptchaVerifier(auth, config.containerId, {
      size: config.size || 'invisible',
      callback: config.callback || (() => {}),
      'expired-callback': config.expiredCallback || (() => {}),
    });

    return recaptchaVerifier;
  } catch (e: any) {
    return null;
  }
};

/**
 * Clean up reCAPTCHA instance
 * @param recaptchaRef - Reference to the RecaptchaVerifier instance
 */
export const cleanupRecaptcha = (recaptchaRef: RecaptchaVerifier | null): void => {
  if (recaptchaRef) {
    try {
      recaptchaRef.clear();
    } catch (e) {
      // Silent fail
    }
  }
};

/**
 * Render reCAPTCHA widget
 * @param recaptchaRef - Reference to the RecaptchaVerifier instance
 */
export const renderRecaptcha = async (recaptchaRef: RecaptchaVerifier | null): Promise<void> => {
  if (!recaptchaRef) return;
  
  try {
    await recaptchaRef.render();
  } catch (renderError) {
    // Silent fail - already rendered or render failed
  }
};

